<template>
  <div
    v-if="(promotionsWithOffers?.length || 0) > 0"
    class="recommended-products"
  >
    <Heading
      tag="h2"
      size="md"
      class="recommended-products__heading"
      font-weight="regular"
      spaces-bottom
    >
      Beliebte Produkte
    </Heading>
    <DraggableScroller
      ref="draggableScrollbarElement"
      has-pagination
      pagination-label="Beliebte Produkte Paginierung"
      show-single-active-pagination
    >
      <ProductOfferTileSMContainer
        :items="promotionsWithOffers"
        tile-type="small"
        class="recommended-products__list-entry"
        heading-tag="h3"
        no-delay-animation
        :animation-init-timeout="5000"
      />
    </DraggableScroller>
  </div>
</template>

<script lang="ts" setup>
import { useLazyAsyncData } from '#imports';
import { Heading } from '@i22team/td-longtail-vue-components';
import ProductOfferTileSMContainer from '~/components/product-offer-tile-s-m-container.vue';
import DraggableScroller from '~/components/draggable-scroller.vue';
import {
  loadOffersForPromotions,
  type Promotion,
  type PromotionOffer,
} from '~/lib/offers-by-group';
import { getPromotionsFromGroup, type DateGroupedPromotions } from '~/lib/promotions';

defineComponent({ name: 'RecommendedProducts' });

interface Recommended extends Promotion {
  teaserTitle?: string;
  description?: string;
  showLocalTitle?: boolean;
  title?: string;
}

const recommendedProductsGrouped: DateGroupedPromotions<Recommended>[] = [
  {
    startDate: '01.11.2026',
    endDate: '17.11.2026 23:59',
    promotions: [
      {
        articleNumber: '4048945048202', // Teufel Rockster GO 2
      },
      {
        articleNumber: '4029164143404', // Homematic IP Starterset Akkukamera
      },
      {
        articleNumber: '4029164142667', // Homematic IP Starter Bundle Heizen Basic für 2 Heizkörper + Wandthermostat Basic
      },
      {
        articleNumber: '0194644146160', // eufy SoloCam S340
      },
      {
        articleNumber: 'EQ140280', // Homematic IP Heizkörperthermostat
      },
      {
        articleNumber: '4260195392359', // Eve Motion (Matter) - Smarter Bewegungssensor
      },
      {
        articleNumber: '4029164137373', // Homematic IP Heizkörperthermostat – basic 6er Set
      },
      {
        articleNumber: '4047976600274', // Homematic IP Fenster- und Türkontakt - Optisch
      },
    ],
  },
  {
    startDate: '01.10.2026',
    endDate: '31.10.2026 23:59',
    promotions: [
      {
        articleNumber: '6970135031481', // Ecovacs DEEBOT X2 COMBO
      },
      {
        articleNumber: '4068568918331', // Eve Energy EU (Matter) 3er Pack
      },
      {
        articleNumber: '4047976621651', // Homematic IP Starter Set Heizen-pure
      },
      {
        articleNumber: '4047443559630', // Kinder-Smartwatch, mit GPS, SOS-Taste, Telefonfunktion, Blau
      },
      {
        articleNumber: '4068568997596', // Eve Door & Window (Matter) 2er Pack
      },
      {
        articleNumber: '4029164142490', // Homematic IP Starter Set Alarm + Außensirene
      },
      {
        articleNumber: '4068568996841', // Eve Thermo (Matter) 3er Pack
      },
      {
        articleNumber: '4029164141912', // Homematic IP Starter Bundle Zutritt + Keypad
      },
    ],
  },
];

const draggableScrollbar = useTemplateRef<InstanceType<typeof DraggableScroller>>(
  'draggableScrollbarElement'
);

const { data: promotionsWithOffers } = useLazyAsyncData<PromotionOffer['offerBySlugs']>(
  'recommended-products',
  async () => {
    const data = await loadOffersForPromotions(
      getPromotionsFromGroup(recommendedProductsGrouped),
      false,
      false
    );

    setTimeout(() => {
      // Update scroller for new pages
      draggableScrollbar.value?.updateScroller();
    }, 1);

    return data;
  }
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.recommended-products {
  &__heading {
    padding-inline: $grid-base;
  }

  :deep(.draggable-scroller__container) {
    list-style-type: none;
    margin-bottom: $grid-base;

    @include mq(false, $mq-sm - 1) {
      scroll-snap-type: x mandatory;
    }
  }

  :deep(.grid-container) {
    max-width: none;
    width: auto;
    padding: 0;
  }

  :deep(.pot-sm-container__row) {
    flex-wrap: nowrap;
    margin-inline: 0;
  }

  :deep(.pot-sm-container__column) {
    padding-inline: $grid-base;
    flex: 0 0 100%;

    @include mq($mq-sm) {
      flex-basis: 50%;
      max-width: 50%;
    }

    @include mq($mq-md) {
      flex-basis: 33.3333%;
      max-width: 33.3333%;
    }

    @include mq($mq-lg) {
      flex-basis: 25%;
    }

    @include mq(false, $mq-sm - 1) {
      scroll-snap-align: start;
    }
  }

  &__hover-image {
    transition: transform 0.3s;
    transform: scale(1.05);
  }

  :deep(.product-offer-tile) {
    .product-offer-tile {
      &__product-image-container {
        justify-content: center;
        img {
          width: 100%;
          aspect-ratio: 1 / 1;
        }
      }

      &__product-image-link {
        width: 100%;

        > a {
          display: block;
        }
      }

      &__image {
        img {
          width: 100%;
          aspect-ratio: 1 / 1;
        }
      }
    }
  }
}
</style>
